import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Upload, User, Users } from 'lucide-react';
import { databaseService, storageService, appwriteConfig, ID } from '../../../lib/appwrite';

export default function Authors() {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    comment: '',
    image: null,
    imagePreview: null
  });

  const AUTHORS_COLLECTION = 'authors';
  const STORAGE_BUCKET = appwriteConfig.storageId || 'reactbucket';

  useEffect(() => {
    loadAuthors();
  }, []);

  const loadAuthors = async () => {
    try {
      setLoading(true);
      const result = await databaseService.listDocuments(AUTHORS_COLLECTION);
      if (result.success) {
        setAuthors(result.data.documents);
      }
    } catch (error) {
      console.error('Error loading authors:', error);
      alert('Failed to load authors');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      designation: '',
      comment: '',
      image: null,
      imagePreview: null
    });
    setEditingAuthor(null);
  };

  const openModal = (author = null) => {
    if (author) {
      setEditingAuthor(author);
      setFormData({
        name: author.name || '',
        designation: author.designation || '',
        comment: author.comment || '',
        image: null,
        imagePreview: author.imageUrl || null
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      
      setFormData(prev => ({ ...prev, image: file }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, imagePreview: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!formData.image) return null;
    
    try {
      const fileId = ID.unique();
      const result = await storageService.uploadFile(STORAGE_BUCKET, fileId, formData.image);
      if (result.success) {
        return storageService.getFileView(STORAGE_BUCKET, result.data.$id);
      }
      return null;
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Author name is required');
      return;
    }

    try {
      setSaving(true);
      
      let imageUrl = editingAuthor?.imageUrl || null;
      
      if (formData.image) {
        const uploadedUrl = await uploadImage();
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        }
      }

      const authorData = {
        name: formData.name.trim(),
        designation: formData.designation.trim(),
        comment: formData.comment.trim(),
        imageUrl: imageUrl,
        updatedAt: new Date().toISOString()
      };

      if (editingAuthor) {
        // Update existing author
        const result = await databaseService.updateDocument(
          AUTHORS_COLLECTION,
          editingAuthor.$id,
          authorData
        );
        if (result.success) {
          alert('Author updated successfully!');
        }
      } else {
        // Create new author
        authorData.createdAt = new Date().toISOString();
        const result = await databaseService.createDocument(AUTHORS_COLLECTION, authorData);
        if (result.success) {
          alert('Author created successfully!');
        }
      }

      closeModal();
      loadAuthors();
    } catch (error) {
      console.error('Error saving author:', error);
      alert('Failed to save author');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (author) => {
    if (!confirm(`Are you sure you want to delete "${author.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const result = await databaseService.deleteDocument(AUTHORS_COLLECTION, author.$id);
      if (result.success) {
        alert('Author deleted successfully!');
        loadAuthors();
      }
    } catch (error) {
      console.error('Error deleting author:', error);
      alert('Failed to delete author');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#4C1D95]">Authors Management</h1>
          <p className="text-gray-500 mt-1">Manage author profiles and information</p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2 bg-[#4C1D95] text-white rounded-lg font-medium hover:bg-[#3b1575] transition-colors shadow-sm"
        >
          <Plus size={20} />
          Add Author
        </button>
      </div>

      {/* Authors Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
          <Users className="w-5 h-5 text-[#4C1D95]" />
          <h2 className="text-lg font-bold text-gray-800">All Authors</h2>
          <span className="text-xs font-medium bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
            {authors.length} Total
          </span>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="text-gray-500">Loading authors...</div>
            </div>
          ) : authors.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {authors.map((author) => (
                <div key={author.$id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                          {author.imageUrl ? (
                            <img 
                              src={author.imageUrl} 
                              alt={author.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{author.name}</h3>
                          {author.designation && (
                            <p className="text-sm text-gray-500">{author.designation}</p>
                          )}
                        </div>
                      </div>
                      
                      {author.comment && (
                        <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                          {author.comment}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => openModal(author)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors"
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(author)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Authors Yet</h3>
              <p className="text-gray-500 mb-6">Get started by adding your first author</p>
              <button
                onClick={() => openModal()}
                className="flex items-center gap-2 px-4 py-2 bg-[#4C1D95] text-white rounded-lg font-medium hover:bg-[#3b1575] transition-colors mx-auto"
              >
                <Plus size={20} />
                Add First Author
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingAuthor ? 'Edit Author' : 'Add New Author'}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Author Image
                </label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                    {formData.imagePreview ? (
                      <img 
                        src={formData.imagePreview} 
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <Upload size={16} />
                      <span className="text-sm font-medium">
                        {formData.image ? 'Change Image' : 'Upload Image'}
                      </span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageChange} 
                        className="hidden" 
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Max 5MB. JPG, PNG, WEBP supported.
                    </p>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter author name"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                  required
                />
              </div>

              {/* Designation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Designation
                </label>
                <input
                  type="text"
                  value={formData.designation}
                  onChange={(e) => setFormData(prev => ({ ...prev, designation: e.target.value }))}
                  placeholder="e.g. Senior Developer, Content Writer"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                />
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio/Comment
                </label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="Brief description about the author..."
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#4C1D95] text-white rounded-lg font-medium hover:bg-[#3b1575] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Save size={18} />
                  {saving ? 'Saving...' : (editingAuthor ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}